import React, {
  useMemo,
  useState,
  useEffect,
  useCallback
} from 'react';
import PropTypes from 'prop-types';

import {
  Flex,
  Label,
  Modal,
  Table,
  Button,
  Spinner,
  Checkbox,
  TableRow,
  Explainer,
  Paragraph,
  TableBody,
  TableCell,
  TableHead,
  TextInput,
  TableHeadCell
} from '@sopranodesign/pulse-components';
import {
  useQuery,
  useMutation
} from '@sopranodesign/pulse-hooks';

import { selectSharePointFolder } from '../../api';

const AddFolderModal = ({ state }) => {
  const {
    refetchSite,
    selectedSite,
    refetchStatus,
    setActiveModal,
    setTokenInvalid
  } = state;

  const [hasCheckedToken, setHasCheckedToken] = useState(false);
  const [selectedFolders, setSelectedFolders] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [errorMessage, setErrorMessage] = useState(null);
  const [fadeError, setFadeError] = useState(false);
  const [sortBy, setSortBy] = useState('name');
  const [sortAsc, setSortAsc] = useState(true);

  const {
    data,
    error,
    refetch,
    isFetching
  } = useQuery({
    queryKey: [`generative-ai/sharepoint/site/${selectedSite?.id}/folder/list`],
    enabled: false
  });

  useEffect(() => {
    if (selectedSite && !hasCheckedToken) {
      refetch()
        .then((result) => {
          const message = result?.error?.response?.data?.message;
          if (message === 'Token is invalid, please reconnect.') {
            setActiveModal(null);
            setTokenInvalid(true);
          }
        })
        .finally(() => {
          setHasCheckedToken(true);
        });
    }
  }, [refetch, selectedSite, setActiveModal, setTokenInvalid, hasCheckedToken]);

  const mutation = useMutation({
    mutationFn: selectSharePointFolder,
    onSuccess: () => {
      setActiveModal(null);
      refetchStatus();
      refetchSite();
    },
    onError: (err) => {
      const backendMessage = err?.response?.data?.message || err?.message || '';
      setErrorMessage(backendMessage || 'Something went wrong. Please try again.');
      setFadeError(false);
      setTimeout(() => {
        setFadeError(true);
        setTimeout(() => setErrorMessage(null), 1000);
      }, 10000);
    }
  });

  const filteredFolders = useMemo(() => {
    if (!data?.data) {
      return [];
    }

    const filtered = data.data.filter(folder =>
      folder.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return filtered.sort((a, b) => {
      const valA = sortBy === 'name' ? a.name.toLowerCase() : a.file_count ?? 0;
      const valB = sortBy === 'name' ? b.name.toLowerCase() : b.file_count ?? 0;
      return typeof valA === 'string'
        ? (sortAsc ? valA.localeCompare(valB) : valB.localeCompare(valA))
        : (sortAsc ? valA - valB : valB - valA);
    });
  }, [data, searchTerm, sortBy, sortAsc]);

  const toggleFolder = useCallback((folder) => {
    setSelectedFolders(prev =>
      prev.some(f => f.id === folder.id)
        ? prev.filter(f => f.id !== folder.id)
        : [...prev, { id: folder.id, name: folder.name }]
    );
  }, []);

  const toggleSelectAll = useCallback(() => {
    setSelectedFolders(prev =>
      prev.length === filteredFolders.length
        ? []
        : filteredFolders.map(folder => ({ id: folder.id, name: folder.name }))
    );
  }, [filteredFolders]);

  const handleConfirm = () => {
    mutation.mutate({ siteId: selectedSite.id, folders: selectedFolders });
  };

  const renderBody = () => {
    if (isFetching) {
      return (
        <Flex col center middle tall>
          <Spinner />
          <Paragraph mt>Loading folders...</Paragraph>
        </Flex>
      );
    }

    if (error && !isFetching) {
      const message = error?.response?.data?.message || error?.message;
      if (message === 'Token is invalid, please reconnect.') {
        return null;
      }

      return (
        <Flex col center middle tall>
          <Explainer type="danger" hideLabel>
            <Paragraph sm danger>
              Failed to load folders. Please try again later.
            </Paragraph>
          </Explainer>
        </Flex>
      );
    }

    return (
      <Flex col tall>
        <Label htmlFor="Search">Search</Label>
        <TextInput
          id="Search"
          placeholder="Search by folder or site name"
          onChange={e => setSearchTerm(e.target.value)}
        />
        {errorMessage && (
          <Explainer type="danger" hideLabel fade={fadeError}>
            <Paragraph sm danger>{errorMessage}</Paragraph>
          </Explainer>
        )}
        <Table>
          <TableHead>
            <TableRow>
              <TableHeadCell style={{ width: '3rem' }}>
                <Checkbox
                  disabled={mutation.isPending}
                  checked={selectedFolders.length === filteredFolders.length && filteredFolders.length > 0}
                  indeterminate={selectedFolders.length > 0 && selectedFolders.length < filteredFolders.length}
                  onChange={toggleSelectAll}
                  aria-label="select-all-folders"
                />
              </TableHeadCell>
              <TableHeadCell style={{ cursor: 'pointer' }}>
                <Button
                  icon={sortBy === 'name' && sortAsc ? 'chevronUp' : 'chevronDown'}
                  variant="inline"
                  colour="black"
                  onClick={() => {
                    setSortBy('name');
                    setSortAsc(prev => sortBy === 'name' ? !prev : true);
                  }}
                  semiBold
                >
                  Folder
                </Button>
              </TableHeadCell>
              <TableHeadCell style={{ cursor: 'pointer' }}>
                <Button
                  icon={sortBy === 'file_count' && sortAsc ? 'chevronUp' : 'chevronDown'}
                  variant="inline"
                  colour="black"
                  onClick={() => {
                    setSortBy('file_count');
                    setSortAsc(prev => sortBy === 'file_count' ? !prev : true);
                  }}
                  semiBold
                >
                  Total Files
                </Button>
              </TableHeadCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredFolders.length > 0 ? (
              filteredFolders.map((folder, index) => (
                <TableRow key={folder.id}>
                  <TableCell style={{ width: '3rem' }}>
                    <Checkbox
                      disabled={mutation.isPending}
                      checked={selectedFolders.some(f => f.id === folder.id)}
                      onChange={() => toggleFolder(folder)}
                      aria-label={`folder - select - ${index + 1}`}
                    />
                  </TableCell>
                  <TableCell>{folder.name}</TableCell>
                  <TableCell>{folder.file_count ?? 0}</TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={3}>
                  <Paragraph>No folders match your search.</Paragraph>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Flex>
    );
  };

  return (
    <Modal
      header={`Site: ${selectedSite.site_name} | Add SharePoint Folder(s)`}
      handleCancel={() => setActiveModal(null)}
      handleConfirm={handleConfirm}
      disableConfirmButton={isFetching || selectedFolders.length === 0 || mutation.isPending}
      confirmText="Confirm"
      cancelText="Cancel"
      body={renderBody()}
    />
  );
};

AddFolderModal.propTypes = {
  state: PropTypes.shape({
    selectedFolder: PropTypes.object,
    refetchSite: PropTypes.func.isRequired,
    refetchStatus: PropTypes.func.isRequired,
    selectedSite: PropTypes.object.isRequired,
    setActiveModal: PropTypes.func.isRequired,
    setTokenInvalid: PropTypes.func.isRequired,
    setSelectedFolder: PropTypes.func.isRequired,
  }).isRequired
};

export default AddFolderModal;